import { Component, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { QrService, RestaurantInfo, MenuItem, MenuData, MenuCategory } from '../../services/qr-service.service';

interface CartItem {
  menuItem: MenuItem;
  quantity: number;
  notes?: string;
  subtotal: number;
}

@Component({
  selector: 'app-menu-viewer',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, FormsModule],
  templateUrl: './menu-viewer.component.html',
  styleUrl: './menu-viewer.component.scss'
})
export class MenuViewerComponent implements OnInit {
  @Input() localId!: string;
  @Input() tableId?: string;
  @Input() viewMode: 'view-only' | 'order-enabled' = 'view-only';

  restaurant: RestaurantInfo | null = null;
  menuData: MenuData | null = null;
  categorizedMenu: { name: string; items: MenuItem[]; }[] = [];
  cart: CartItem[] = [];
  selectedCategory = '';
  loading = false;
  error = '';
  
  showCart = false;
  showItemDetails = false;
  selectedItem: MenuItem | null = null;
  itemQuantity = 1;
  itemNotes = '';

  searchForm: FormGroup;
  filterForm: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private qrService: QrService,
    private fb: FormBuilder
  ) {
    this.searchForm = this.fb.group({
      search: ['']
    });

    this.filterForm = this.fb.group({
      category: ['all'],
      maxPrice: [50],
      allergens: ['all'],
      available: [true]
    });
  }

  ngOnInit(): void {
    // Obtener parámetros de la ruta si no se pasan como Input
    if (!this.localId) {
      this.localId = this.route.snapshot.params['localId'];
      this.tableId = this.route.snapshot.params['tableId'];
      this.viewMode = this.route.snapshot.queryParams['mode'] || 'view-only';
    }

    this.loadRestaurantInfo();
    this.loadMenu();

    // Suscribirse a actualizaciones de menú en tiempo real
    this.qrService.currentMenu$.subscribe((menuData) => {
      if (menuData && menuData.restaurant_id === this.localId) {
        console.log('🔄 Real-time menu update received:', menuData);
        this.menuData = menuData;
        this.categorizeMenu();
        this.applyFilters();
      }
    });

    // Suscribirse a cambios en búsqueda y filtros
    this.searchForm.valueChanges.subscribe(() => this.applyFilters());
    this.filterForm.valueChanges.subscribe(() => this.applyFilters());
  }

  loadRestaurantInfo(): void {
    this.loading = true;
    this.qrService.getRestaurantInfo(this.localId).subscribe({
      next: (restaurant) => {
        this.restaurant = restaurant;
        this.loading = false;
      },
      error: (error) => {
        this.error = 'Error cargando información del restaurante';
        this.loading = false;
        console.error(error);
      }
    });
  }

  loadMenu(): void {
    this.qrService.getMenu(this.localId).subscribe({
      next: (menuData) => {
        this.menuData = menuData;
        this.categorizeMenu();
        this.applyFilters();
      },
      error: (error) => {
        this.error = 'Error cargando el menú';
        console.error(error);
      }
    });
  }

  categorizeMenu(): void {
    if (!this.menuData || !this.menuData.categories) {
      this.categorizedMenu = [];
      return;
    }

    this.categorizedMenu = this.menuData.categories.map(category => ({
      name: category.name,
      items: category.items || []
    }));

    if (this.categorizedMenu.length > 0 && !this.selectedCategory) {
      this.selectedCategory = this.categorizedMenu[0].name;
    }
  }

  applyFilters(): void {
    if (!this.menuData || !this.menuData.categories) {
      this.categorizedMenu = [];
      return;
    }

    const searchTerm = this.searchForm.get('search')?.value?.toLowerCase() || '';
    const filters = this.filterForm.value;
    
    let filteredCategories = this.menuData.categories.map(category => {
      let filteredItems = category.items || [];

      // Filtro por búsqueda
      if (searchTerm) {
        filteredItems = filteredItems.filter(item =>
          item.name.toLowerCase().includes(searchTerm) ||
          item.description.toLowerCase().includes(searchTerm)
        );
      }

      // Filtro por precio máximo
      if (filters.maxPrice) {
        filteredItems = filteredItems.filter(item => item.price <= filters.maxPrice);
      }

      // Filtro por alérgenos
      if (filters.allergens !== 'all') {
        filteredItems = filteredItems.filter(item => 
          !item.allergens || !item.allergens.includes(filters.allergens)
        );
      }

      // Filtro por disponibilidad
      if (filters.available) {
        filteredItems = filteredItems.filter(item => item.available);
      }

      return {
        name: category.name,
        items: filteredItems
      };
    });

    // Filtro por categoría específica
    if (filters.category !== 'all') {
      filteredCategories = filteredCategories.filter(category => category.name === filters.category);
    }

    // Solo mostrar categorías con elementos
    this.categorizedMenu = filteredCategories.filter(category => category.items.length > 0);
  }

  selectCategory(category: string): void {
    this.selectedCategory = category;
  }

  showItemDetail(item: MenuItem): void {
    this.selectedItem = item;
    this.itemQuantity = 1;
    this.itemNotes = '';
    this.showItemDetails = true;
  }

  closeItemDetail(): void {
    this.showItemDetails = false;
    this.selectedItem = null;
  }

  addToCart(item: MenuItem, quantity: number = 1, notes?: string): void {
    if (this.viewMode === 'view-only') {
      return;
    }

    const existingItemIndex = this.cart.findIndex(cartItem => 
      cartItem.menuItem.id === item.id && cartItem.notes === notes
    );

    if (existingItemIndex > -1) {
      this.cart[existingItemIndex].quantity += quantity;
      this.cart[existingItemIndex].subtotal = this.cart[existingItemIndex].quantity * item.price;
    } else {
      this.cart.push({
        menuItem: item,
        quantity,
        notes,
        subtotal: quantity * item.price
      });
    }

    this.closeItemDetail();
  }

  removeFromCart(index: number): void {
    this.cart.splice(index, 1);
  }

  updateCartItemQuantity(index: number, quantity: number): void {
    if (quantity <= 0) {
      this.removeFromCart(index);
    } else {
      this.cart[index].quantity = quantity;
      this.cart[index].subtotal = quantity * this.cart[index].menuItem.price;
    }
  }

  toggleCart(): void {
    this.showCart = !this.showCart;
  }

  getCartTotal(): number {
    return this.cart.reduce((total, item) => total + item.subtotal, 0);
  }

  getCartItemCount(): number {
    return this.cart.reduce((count, item) => count + item.quantity, 0);
  }

  canOrderToBar(): boolean {
    return this.viewMode === 'order-enabled' && 
           this.restaurant?.features.barOrderingEnabled === true;
  }

  canOrderToTable(): boolean {
    return this.viewMode === 'order-enabled' && 
           this.restaurant?.features.tableServiceEnabled === true &&
           !!this.tableId;
  }

  async proceedToOrder(orderType: 'bar' | 'table'): Promise<void> {
    if (this.cart.length === 0) {
      alert('Tu carrito está vacío');
      return;
    }

    try {
      // Validar pedido
      const validation = await this.qrService.validateOrder(this.localId, this.tableId);
      
      if (!validation.isValid) {
        alert(`No se puede realizar el pedido:\n${validation.errors.join('\n')}`);
        return;
      }

      // Navegar a confirmación de pedido
      this.router.navigate(['/order-confirmation'], {
        queryParams: {
          localId: this.localId,
          tableId: this.tableId,
          orderType,
          cartData: JSON.stringify(this.cart.map(item => ({
            menuItemId: item.menuItem.id,
            quantity: item.quantity,
            notes: item.notes
          })))
        }
      });

    } catch (error) {
      console.error('Error procesando pedido:', error);
      alert('Error al procesar el pedido. Inténtalo de nuevo.');
    }
  }

  // Computed properties para el template
  get currentCategoryItems(): MenuItem[] {
    const category = this.categorizedMenu.find(cat => cat.name === this.selectedCategory);
    return category ? category.items : [];
  }

  get categories(): string[] {
    return this.categorizedMenu.map(cat => cat.name);
  }

  get allergensList(): string[] {
    const allergens = new Set<string>();
    if (this.menuData && this.menuData.categories) {
      this.menuData.categories.forEach(category => {
        if (category.items) {
          category.items.forEach(item => {
            if (item.allergens) {
              item.allergens.forEach(allergen => allergens.add(allergen));
            }
          });
        }
      });
    }
    return Array.from(allergens);
  }

  formatPrice(price: number): string {
    return `${price.toFixed(2)}€`;
  }

  formatPreparationTime(minutes?: number): string {
    if (!minutes || minutes < 60) {
      return `${minutes || 0} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
  }

  getItemImageUrl(item: MenuItem): string {
    return item.image || '/assets/images/default-food.jpg';
  }

  shareMenu(): void {
    if (navigator.share) {
      navigator.share({
        title: `Menú de ${this.restaurant?.name}`,
        text: `Echa un vistazo al menú de ${this.restaurant?.name}`,
        url: window.location.href
      });
    } else {
      // Fallback para navegadores sin Web Share API
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('Enlace copiado al portapapeles');
      });
    }
  }
}